import { useGetUserProfileByAddress } from '@/api/query'
import { GradientText } from '@/components/atoms/GradientText/GradientText'
import { useJackpot } from '@/context/JackpotProvider'
import { AppModals, JackpotModals, useAppModal } from '@/context/ModalProvider'
import { Flex, SimpleGrid, Text, Box } from '@chakra-ui/react'
import { Observer } from 'mobx-react'
import { Avatar } from '@/components/atoms/Avatar'
import { computed } from 'mobx'
import { BetPanel } from '../molecules/BetPanel'
import { SHU_DENOM } from '@/constants/constants'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { useTranslation } from 'next-i18next/pages'

export const JackpotPlayersBets = () => {
  const jackpot = useJackpot()
  const isRoundStarted = computed(() => jackpot.state.bets.length)

  return (
    <Observer>
      {() =>
        isRoundStarted.get() ? (
          <>
            <JackpotPlayers />
            <JackpotBets />
          </>
        ) : null
      }
    </Observer>
  )
}

const JackpotBets = () => {
  const { t } = useTranslation()
  const modal = useAppModal()
  const jackpot = useJackpot()

  return (
    <Box>
      <Text color={'ninja.300'} fontWeight={700} mb={3}>
        {t('Jackpot.bets-history')}
      </Text>
      <Observer>
        {() => (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, '2xl': 5 }} gap={2} mb={6}>
            {jackpot.state.bets.map((bet) => (
              <BetPanel
                amount={bet.amount}
                address={bet.address}
                key={bet.address + bet.block}
                badges={[
                  [
                    t('common.txhash'),
                    bet.tx_hash.slice(0, 3) + '...' + bet.tx_hash.slice(-3),
                    () => modal.openModalNew(JackpotModals.BlockStats, { player: { ...bet } }),
                  ],
                ]}
              />
            ))}
          </SimpleGrid>
        )}
      </Observer>
    </Box>
  )
}

const JackpotPlayers = () => {
  const { t } = useTranslation()
  const jackpot = useJackpot()

  if (!jackpot.state.players.length) {
    return null
  }

  return (
    <>
      <Text color={'ninja.300'} fontWeight={700} mb={3}>
        {t('Jackpot.players')}
      </Text>
      <Observer>
        {() => (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 2, xl: 3, '2xl': 4 }} gap={2} mb={6}>
            {jackpot.state.players
              .slice()
              .sort((a, b) => parseFloat(b.chance) - parseFloat(a.chance))
              .map((player) => (
                <PlayerPanel
                  address={player.address}
                  amount={player.amount}
                  key={player.address}
                  badges={[[t('Jackpot.chance'), player.chance + '%']]}
                />
              ))}
          </SimpleGrid>
        )}
      </Observer>
    </>
  )
}

const PlayerPanel = ({
  address,
  amount,
  badges,
}: {
  address: string
  amount: string
  badges: Array<[string, string, () => void] | [string, string]>
}) => {
  const { data } = useGetUserProfileByAddress(address)
  const modal = useAppModal()
  const numberOfNfts = data?.nfts?.totalCount
  const isNftOwner = Boolean(numberOfNfts)

  return (
    <Box bg={'ninja.600'} position={'relative'} overflow={'hidden'} rounded={'xl'} p={4}>
      <Flex justifyContent={'space-between'} mb={3}>
        <Flex alignItems={'center'} gap={3}>
          <Box
            border={'1px solid'}
            borderColor={'transparent'}
            transition={'.2s'}
            p={0.5}
            _hover={{
              borderColor: 'ninja.400',
            }}
            rounded={'full'}
          >
            <Avatar
              cursor={'pointer'}
              onClick={() => modal.openModalNew(AppModals.UserStats, { address })}
              boxShadow={isNftOwner ? '0px 0px 10px 0px #5D52EE;' : 'none'}
              src={data?.user?.profileAvatar || ''}
              size={'lg'}
            />
          </Box>
          {isNftOwner ? (
            <GradientText
              numberOfNfts={numberOfNfts!}
              props={{
                fontWeight: 700,
              }}
            >
              {data?.user?.profileUserName}
            </GradientText>
          ) : (
            <Text fontWeight={700} color={'ninja.100'}>
              {data?.user?.profileUserName}
            </Text>
          )}
        </Flex>
        <Flex alignItems={'center'} gap={1.5}>
          <FiatOrCryptoAmount
            iconSize={5}
            value={new TokenMoney(amount, SHU_DENOM)}
            wrapperStyles={{ fontSize: 'sm', fontWeight: 700 }}
            currencyStyles={{
              display: 'none',
            }}
          />
        </Flex>
      </Flex>
      {badges.map(([title, value, callback], index) => {
        return (
          <Flex
            onClick={() => callback?.()}
            cursor={callback ? 'pointer' : 'auto'}
            key={index}
            py={1.5}
            px={4}
            gap={2}
            rounded={'full'}
            bg={'ninja.500'}
            w={'min-content'}
            alignItems={'center'}
            justifyContent={'space-between'}
            _hover={{
              color: callback ? 'ninja.100' : 'brand.white',
            }}
          >
            <Text color={'ninja.200'} fontWeight={500} fontSize={'sm'}>
              {title}
            </Text>
            <Text as={'span'} fontSize={'sm'}>
              {value}
            </Text>
          </Flex>
        )
      })}
      <Box position={'absolute'} bottom={0} left={0} right={0} bg={'ninja.400'} h={1} />
    </Box>
  )
}
